/**
 * BUILDER STREAM MODEL — the pure (React-free) shaping of a claude-code cc-event stream into what the Dock renders
 * (AGENT-HARNESS.md §15). Raw events in → groups out:
 *  - a tool row exists the moment the tool STARTS (``tool_call`` pending, input {}), its input/result fill in later;
 *  - reasoning streams into ONE live "Thinking…" block (``thinking`` + ``delta``), closed by the next step;
 *  - each message's text is its own paragraph (``text_end`` per message);
 *  - runs of read-only exploration fold into one row; the agent's todo list is pinned; one "now doing" line.
 */

// ─── Dedup loaded events (collapse cumulative stream-json replays) ───────────

export function deduplicateLoadedEvents(events) {
  // For completed sessions loaded from S3, stream-json replays prior text
  // segments in each subsequent assistant turn. Fix: deduplicate text segments
  // by exact content, keep only first occurrence. Tool calls dedup by ID.
  const cleaned = [];
  const seenToolIds = new Set();
  const seenThinking = new Set();
  const seenTexts = new Set();
  let buf = '';

  for (const e of events) {
    const cc = e.cc || '';
    if (cc === 'text_delta') {
      buf += e.text || '';
    } else if (cc === 'text_end') {
      const text = buf.trim();
      buf = '';
      if (text && !seenTexts.has(text)) {
        seenTexts.add(text);
        cleaned.push({ cc: 'text', text });
      }
    } else {
      // Non-text event — flush any partial buffer
      if (buf.trim() && !seenTexts.has(buf.trim())) {
        seenTexts.add(buf.trim());
        cleaned.push({ cc: 'text', text: buf.trim() });
      }
      buf = '';

      if (cc === 'init') {
        if (!cleaned.some((c) => c.cc === 'init')) cleaned.push(e);
      } else if (cc === 'text') {
        // L119: a persisted NOTE line (run facts, the final-answer pointer, input check, turn summary,
        // the Planner's run log). Dropping it left avatar logs near-empty and the Planner's BLANK.
        const t = (e.text || '').trim();
        if (t && !seenTexts.has(t)) { seenTexts.add(t); cleaned.push({ cc: 'text', text: t }); }
      } else if (cc === 'tool_call') {
        if (e.id && seenToolIds.has(e.id)) continue;
        if (e.id) seenToolIds.add(e.id);
        cleaned.push(e);
      } else if (cc === 'tool_result') {
        cleaned.push(e);
      } else if (cc === 'thinking') {
        const t = e.text || '';
        if (t && !seenThinking.has(t)) { seenThinking.add(t); cleaned.push(e); }
      } else if (cc === 'thinking_start') {
        // skip for loaded sessions
      } else if (cc === 'usage' || cc === 'result') {
        cleaned.push(e);
      } else if (cc === 'error' || cc === 'rate_limit' || cc === 'policy_block') {
        // L119: a finished run's error / rate-limit / policy banner vanished on reload (live only).
        cleaned.push(e);
      }
    }
  }
  // Flush trailing buffer
  if (buf.trim() && !seenTexts.has(buf.trim())) {
    cleaned.push({ cc: 'text', text: buf.trim() });
  }
  return cleaned;
}

// ─── Event grouping: correlate tool_call + tool_result ──────────────────────

export function groupEvents(events) {
  const groups = [];
  const pendingCalls = new Map();
  const seenToolIds = new Set();
  const seenThinkingTexts = new Set();
  let initEvent = null;
  let resultEvent = null;

  // The next real step ENDS an open streamed reasoning block (the provider sends no explicit end live).
  const closeThinking = (evt) => {
    for (let j = groups.length - 1; j >= 0; j--) {
      const g = groups[j];
      if (g.type === 'thinking_live') {   // (an empty one = hidden reasoning: still ends, as "Thinking (Ns)")
        groups[j] = { type: 'thinking_done', text: g.text || '', startTs: g.startTs, endTs: evt.ts || Date.now() };
        if (g.text) seenThinkingTexts.add(g.text);
      }
      if (g.type === 'thinking_live' || g.type === 'thinking_done') break;
    }
  };

  for (const evt of events) {
    if (evt.cc === 'init') {
      initEvent = evt;
    } else if (evt.cc === 'tool_call') {
      closeThinking(evt);
      // The SAME id again = the live start row (pending, input {}) gaining its real input — merge, never a 2nd row.
      if (evt.id && seenToolIds.has(evt.id)) {
        const g = pendingCalls.get(evt.id);
        if (g && evt.input && Object.keys(evt.input).length) g.call = { ...g.call, ...evt, pending: false };
        continue;
      }
      if (evt.id) seenToolIds.add(evt.id);
      // A tool starting ENDS the text before it (no stale typing cursor on a finished sentence).
      for (let j = groups.length - 1; j >= 0; j--) {
        if (groups[j].type === 'streaming_text') { groups[j].done = true; break; }
        if (groups[j].type === 'tool') break;
      }
      const group = { call: evt, result: null };
      pendingCalls.set(evt.id, group);
      groups.push({ type: 'tool', group });
    } else if (evt.cc === 'tool_result' && evt.id && pendingCalls.has(evt.id)) {
      pendingCalls.get(evt.id).result = evt;
    } else if (evt.cc === 'tool_result') {
      groups.push({ type: 'tool', group: { call: null, result: evt } });
    } else if (evt.cc === 'text_delta') {
      closeThinking(evt);
      // Accumulate deltas into the last open streaming_text group
      const last = groups[groups.length - 1];
      if (last && last.type === 'streaming_text' && !last.done) {
        last.text += evt.text;
      } else {
        groups.push({ type: 'streaming_text', text: evt.text, done: false });
      }
    } else if (evt.cc === 'text_end') {
      // Mark the open streaming text as done — the LATEST open one, even when a tool row started after it
      // (a message = [text, tool_use]: the tool row lands before the message's text_end).
      let last = null;
      for (let j = groups.length - 1; j >= 0; j--) {
        if (groups[j].type === 'streaming_text') { if (!groups[j].done) last = groups[j]; break; }
      }
      if (last) {
        last.done = true;
        // Dedup: if this text starts with an earlier completed text block's content,
        // the earlier one was a partial replay — remove it and keep this (the longer one)
        for (let j = groups.length - 2; j >= 0; j--) {
          const prev = groups[j];
          if (prev !== last && prev.type === 'streaming_text' && prev.done && last.text.startsWith(prev.text)) {
            groups.splice(j, 1);
            break;
          }
        }
      }
    } else if (evt.cc === 'usage') {
      // Usage events are tracked at the top level, not rendered inline
      groups.push({
        type: 'usage',
        input: evt.input,
        output: evt.output,
        cacheRead: evt.cacheRead || 0,
        cacheCreation: evt.cacheCreation || 0,
      });
    } else if (evt.cc === 'thinking_start') {
      // Only add one thinking_live at a time
      if (!groups.some((g) => g.type === 'thinking_live')) {
        groups.push({ type: 'thinking_live', startTs: evt.ts || Date.now() });
      }
    } else if (evt.cc === 'thinking' && evt.delta) {
      // A live FRAGMENT of the open reasoning block — append to it (one block, not one per fragment).
      let live = null;
      for (let j = groups.length - 1; j >= 0; j--) {
        if (groups[j].type === 'thinking_live') { live = groups[j]; break; }
      }
      if (!live) { live = { type: 'thinking_live', startTs: evt.ts || Date.now(), text: '' }; groups.push(live); }
      live.text = (live.text || '') + (evt.text || '');
    } else if (evt.cc === 'thinking') {
      // Deduplicate thinking text
      if (seenThinkingTexts.has(evt.text)) continue;
      seenThinkingTexts.add(evt.text);
      // Find last thinking_live and convert to thinking_done
      for (let j = groups.length - 1; j >= 0; j--) {
        if (groups[j].type === 'thinking_live') {
          groups[j] = {
            type: 'thinking_done',
            text: evt.text,
            startTs: groups[j].startTs,
            endTs: evt.ts || Date.now(),
          };
          break;
        }
      }
      // If no thinking_live found, just push as thinking_done
      if (!groups.some((g) => g.type === 'thinking_done' && g.text === evt.text)) {
        groups.push({ type: 'thinking_done', text: evt.text, startTs: null, endTs: null });
      }
    } else if (evt.cc === 'result') {
      resultEvent = evt;
      groups.push({ type: 'result', evt });
    } else if (evt.cc === 'error' || evt.cc === 'rate_limit') {
      // Surface SDK-level errors and rate-limit warnings inline in the
      // stream so the user understands stalls / failures.
      groups.push({ type: 'banner', evt });
    } else if (evt.cc === 'policy_block') {
      // L119: a denied tool call (e.g. a write to the default branch) — it never rendered, live or loaded.
      groups.push({ type: 'banner', evt: { cc: 'error', severity: 'warning',
        text: `Blocked by policy: ${evt.tool || 'tool call'}${evt.branch ? ` → ${evt.branch}` : ''}`
          + `${evt.reason ? ` (${String(evt.reason).replace(/_/g, ' ')})` : ''}` } });
    } else if (evt.cc === 'image' || (evt.cc === 'content_block' && evt.contentType === 'image')) {
      groups.push({ type: 'image' });
    } else {
      // Unknown content block types get a pill
      if (evt.cc === 'content_block' && evt.contentType && evt.contentType !== 'text') {
        groups.push({ type: 'unknown_block', contentType: evt.contentType });
      } else {
        groups.push({ type: 'event', evt });
      }
    }
  }
  return { groups, initEvent, resultEvent };
}


// ─── Exploration folding ────────────────────────────────────────────────────

/** Read-only look-around tools — a run of these folds into ONE row ("Read 6 files · searched 2 patterns"). */
export const EXPLORE_TOOLS = new Set(['Read', 'Grep', 'Glob', 'LS', 'NotebookRead', 'WebFetch', 'WebSearch',
  'mcp__stepflow-web__web_search', 'mcp__stepflow-web__web_visit']);

/** Consecutive (≥ 2) exploration tool groups → one ``{type:'explore', items}`` group. Order is kept. */
export function foldExplorations(groups, minRun = 2) {
  const out = [];
  let run = [];
  const flush = () => {
    if (run.length >= minRun) out.push({ type: 'explore', items: run });
    else out.push(...run);
    run = [];
  };
  for (const g of groups) {
    if (g.type === 'tool' && EXPLORE_TOOLS.has(g.group?.call?.tool)) { run.push(g); continue; }
    flush();
    out.push(g);
  }
  flush();
  return out;
}

/** "Read 4 files · searched 2 patterns · fetched 1 page" for an explore group. */
export function exploreSummary(items) {
  const n = { read: new Set(), search: 0, web: 0 };
  for (const it of items) {
    const { tool, input = {} } = it.group?.call || {};
    if (tool === 'Read' || tool === 'NotebookRead') n.read.add(input.file_path || input.notebook_path || it.group.call.id);
    else if (tool === 'Grep' || tool === 'Glob' || tool === 'LS') n.search += 1;
    else n.web += 1;
  }
  const parts = [];
  if (n.read.size) parts.push(`Read ${n.read.size} file${n.read.size === 1 ? '' : 's'}`);
  if (n.search) parts.push(`searched ${n.search} pattern${n.search === 1 ? '' : 's'}`);
  if (n.web) parts.push(`${n.web} web lookup${n.web === 1 ? '' : 's'}`);
  return parts.join(' · ');
}

// ─── Todo list (TodoWrite) ──────────────────────────────────────────────────

/** The agent's LATEST todo list (each TodoWrite replaces the whole list), or null. */
export function latestTodos(events) {
  for (let i = (events || []).length - 1; i >= 0; i--) {
    const e = events[i];
    if (e?.cc === 'tool_call' && e.tool === 'TodoWrite' && Array.isArray(e.input?.todos)) {
      return e.input.todos.map((t) => ({
        content: String(t.content || ''), activeForm: String(t.activeForm || t.content || ''),
        status: ['completed', 'in_progress'].includes(t.status) ? t.status : 'pending',
      }));
    }
  }
  return null;
}

// ─── "Now doing" ────────────────────────────────────────────────────────────

function _short(p) {
  if (!p) return '';
  const parts = String(p).split('/');
  return parts.length <= 2 ? String(p) : parts.slice(-2).join('/');
}

const _VERB = {
  Read: (i) => `Reading ${_short(i.file_path)}`, Edit: (i) => `Editing ${_short(i.file_path)}`,
  Write: (i) => `Writing ${_short(i.file_path)}`, Bash: (i) => (i.description || `Running ${String(i.command || '').slice(0, 60)}`),
  Grep: (i) => `Searching for ${String(i.pattern || '').slice(0, 40)}`, Glob: (i) => `Finding ${i.pattern || 'files'}`,
  Agent: (i) => (i.description ? `Delegating: ${i.description}` : 'Delegating to a sub-agent'),
  TodoWrite: () => 'Updating the plan', WebFetch: (i) => `Fetching ${String(i.url || '').slice(0, 60)}`,
};

/** One honest line for the live footer: the running tool, else open reasoning/writing, else the in-progress todo. */
export function nowDoing(groups, todos) {
  for (let j = (groups || []).length - 1; j >= 0; j--) {
    const g = groups[j];
    const tools = g.type === 'explore' ? g.items : g.type === 'tool' ? [g] : [];
    const running = tools.filter((t) => t.group?.call && !t.group.result);
    if (running.length) {
      const { tool, input = {} } = running[running.length - 1].group.call;
      const f = _VERB[tool];
      const label = (f ? f(input) : `Using ${String(tool || 'a tool').replace(/^mcp__[^_]+__/, '')}`).trim();
      return label.replace(/\s+$/, '') || 'Working';
    }
    if (g.type === 'thinking_live') return 'Thinking';
    if (g.type === 'streaming_text' && !g.done) return 'Writing';
    if (tools.length || g.type === 'thinking_done' || g.type === 'streaming_text') break;
  }
  const doing = (todos || []).find((t) => t.status === 'in_progress');
  return doing ? doing.activeForm : 'Working';
}

// ─── Edit stats ─────────────────────────────────────────────────────────────

/** {added, removed} lines for an Edit / MultiEdit / Write call (from its input — always present), or null. */
export function editStats(tool, input) {
  if (!input || typeof input !== 'object') return null;
  const lines = (s) => (s ? String(s).split('\n').length : 0);
  if (tool === 'Edit') return { added: lines(input.new_string), removed: lines(input.old_string) };
  if (tool === 'MultiEdit' && Array.isArray(input.edits)) {
    return input.edits.reduce((a, e) => ({ added: a.added + lines(e.new_string), removed: a.removed + lines(e.old_string) }),
      { added: 0, removed: 0 });
  }
  if (tool === 'Write') return { added: lines(input.content), removed: 0 };
  return null;
}
